import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CreditCard, Download, Smartphone } from 'lucide-react';
import {
  CANCELLATION_REASONS,
  MANDATE_METHODS,
  type BillingState,
  type CancellationReason,
  type MandateMethod,
  type PlanKey,
} from '@tailoring-crm/shared';
import { API_BASE_URL } from '../../lib/api/base-url.js';
import { formatCurrency, formatDateLong } from '../../lib/format.js';
import { invoicePath } from '../../lib/billing/payments-api.js';
import {
  useBillingHistory,
  useCancelSubscription,
  useStartCheckout,
} from '../../lib/billing/use-billing.js';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  useToast,
  cn,
} from '@tailoring-crm/ui';

/**
 * How the shop pays: the mandate, what has been collected, and the way out.
 *
 * Three things this section does deliberately.
 *
 * **UPI is offered first.** Not because it is technically neater but because it is what this
 * market has. A boutique owner in Coimbatore has a UPI app; whether they have a card that
 * supports recurring mandates is a coin toss.
 *
 * **"Pay by link instead" is offered as plainly as the mandate.** A shop that will not set up a
 * standing instruction is still a paying customer, and burying that option would be deciding
 * not to sell to them.
 *
 * **Cancelling is easy and says what happens.** No retention offer, no three-step flow. The
 * reason is asked for because we want to know, and the screen states — before the button, not
 * after — that nothing is deleted and the shop keeps what it has already paid for.
 */

const METHOD_ICONS: Readonly<Record<MandateMethod, typeof CreditCard>> = Object.freeze({
  upi: Smartphone,
  card: CreditCard,
  netbanking: CreditCard,
  manual: Download,
});

export function PaymentSection({
  state,
  planKey,
}: {
  state: BillingState;
  planKey: PlanKey;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const checkout = useStartCheckout();
  const history = useBillingHistory();
  const cancel = useCancelSubscription();

  const [method, setMethod] = useState<MandateMethod>('upi');
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState<CancellationReason>('too_expensive');
  const [detail, setDetail] = useState('');

  async function start(chosen: MandateMethod): Promise<void> {
    try {
      const handoff = await checkout.mutateAsync({
        planKey,
        billingCycle: state.billingCycle,
        mandateMethod: chosen,
      });
      if (handoff.authorisationUrl) {
        // Straight to the gateway. This product never sees a card number or a UPI PIN.
        window.location.assign(handoff.authorisationUrl);
      }
    } catch {
      toast({ title: t('billing.checkoutFailed'), variant: 'destructive' });
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* --- What is going wrong, if anything ------------------------------------------- */}
      {state.status === 'past_due' ? (
        <div
          role="status"
          className="flex flex-col gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm"
        >
          <span className="flex items-center gap-2 font-semibold">
            <AlertTriangle aria-hidden="true" className="size-4 text-destructive" />
            {t('billing.paymentFailedTitle')}
          </span>
          <span>
            {t('billing.paymentFailedBody', { count: state.graceDaysRemaining ?? 0 })}
            {state.readOnlyFrom
              ? ` ${t('billing.readOnlyFrom', { date: formatDateLong(state.readOnlyFrom) })}`
              : ''}
          </span>
          <div>
            <Button size="sm" onClick={() => void start(state.mandate?.method ?? method)}>
              {t('billing.retryNow')}
            </Button>
          </div>
        </div>
      ) : null}

      {state.status === 'read_only' ? (
        <div
          role="status"
          className="flex flex-col gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm"
        >
          <span className="font-semibold">{t('billing.readOnlyTitle')}</span>
          {/* The reassurance is the message. Nothing has been taken away. */}
          <span>{t('billing.readOnlyBody')}</span>
        </div>
      ) : null}

      {/* --- The mandate --------------------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>{t('billing.paymentMethod')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {state.mandate && state.mandate.startedAt ? (
            <p className="text-sm" role="status">
              {t(`billing.method.${state.mandate.method}`)} ·{' '}
              {formatCurrency(state.mandate.amountPaise)} ·{' '}
              {t(`billing.cycle.${state.billingCycle}`)}
              {state.mandate.currentPeriodEnd
                ? ` · ${t('billing.nextCharge', {
                    date: formatDateLong(state.mandate.currentPeriodEnd),
                  })}`
                : ''}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">{t('billing.noMandate')}</p>
          )}

          <div
            role="radiogroup"
            aria-label={t('billing.paymentMethod')}
            className="flex flex-wrap gap-2"
          >
            {MANDATE_METHODS.map((one) => {
              const Icon = METHOD_ICONS[one];
              return (
                <button
                  key={one}
                  type="button"
                  role="radio"
                  aria-checked={method === one}
                  onClick={() => setMethod(one)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    method === one
                      ? 'border-transparent bg-primary text-primary-foreground'
                      : 'border-border hover:bg-accent',
                  )}
                >
                  <Icon aria-hidden="true" className="size-3.5" />
                  {t(`billing.method.${one}`)}
                </button>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground">{t(`billing.methodHint.${method}`)}</p>

          <div>
            <Button disabled={checkout.isPending} onClick={() => void start(method)}>
              {state.mandate?.startedAt ? t('billing.changeMethod') : t('billing.subscribe')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* --- What has been collected ---------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>{t('billing.history')}</CardTitle>
        </CardHeader>
        <CardContent>
          {history.data && history.data.items.length > 0 ? (
            <ul className="flex flex-col gap-1.5">
              {history.data.items.map((payment) => (
                <li
                  key={payment.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm"
                >
                  <span className="min-w-0">
                    <span className="block font-medium">
                      {formatCurrency(payment.amountPaise)} ·{' '}
                      {t(`billing.paymentStatus.${payment.status}`)}
                    </span>
                    <span className="block text-muted-foreground">
                      {formatDateLong(payment.paidAt)}
                      {payment.failureReason ? ` · ${payment.failureReason}` : ''}
                    </span>
                  </span>
                  {payment.invoiceNumber ? (
                    <Button asChild variant="outline" size="sm">
                      <a href={`${API_BASE_URL}${invoicePath(payment.id)}`}>
                        <Download aria-hidden="true" />
                        {payment.invoiceNumber}
                      </a>
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{t('billing.noHistory')}</p>
          )}
        </CardContent>
      </Card>

      {/* --- Leaving --------------------------------------------------------------------- */}
      {state.status !== 'cancelled' ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('billing.cancelTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {/* Said before the button, not after: afterwards it arrives too late to reassure. */}
            <p className="text-sm text-muted-foreground">{t('billing.cancelReassurance')}</p>

            {cancelling ? (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="cancel-reason">{t('billing.cancelReasonLabel')}</Label>
                  <select
                    id="cancel-reason"
                    value={reason}
                    onChange={(event) => setReason(event.target.value as CancellationReason)}
                    className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                  >
                    {CANCELLATION_REASONS.map((one) => (
                      <option key={one} value={one}>
                        {t(`billing.cancelReason.${one}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="cancel-detail">{t('billing.cancelDetailLabel')}</Label>
                  <Input
                    id="cancel-detail"
                    value={detail}
                    maxLength={500}
                    onChange={(event) => setDetail(event.target.value)}
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="destructive"
                    disabled={cancel.isPending}
                    onClick={() => {
                      void cancel
                        .mutateAsync({
                          reason,
                          immediately: false,
                          ...(detail.trim() ? { detail: detail.trim() } : {}),
                        })
                        .then(() => {
                          setCancelling(false);
                          toast({ title: t('billing.cancelled') });
                        })
                        .catch(() => {
                          toast({ title: t('error.unexpected'), variant: 'destructive' });
                        });
                    }}
                  >
                    {t('billing.cancelConfirm')}
                  </Button>
                  <Button variant="ghost" onClick={() => setCancelling(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </>
            ) : (
              <div>
                <Button variant="outline" onClick={() => setCancelling(true)}>
                  {t('billing.cancelAction')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
